import type { AjoutControle } from '@rosetta/shared';
import type { ReactNode } from 'react';
import { MarqueBulle } from './marque-bulle';
import { PuceCode } from './puce-code';

/**
 * The look of an addition, in the text and in the key: `rouge-tint` as a background alone, with no
 * underline (decision of 2026-10-12, #25), whatever the status Tahiti gives the indicator 2.3
 * (decision of 2026-10-13, #30: red is the colour of the additions, always; "À relire" keeps its
 * yellow on the counters and the cards). The tint is not the only information (WCAG 1.4.1): the
 * passage has a name ("Ajout : ..."), a bubble that says "ajout" and a key in the legend. The
 * highlight of the scroll to it is an outline, like the focus, so the size does not change.
 */
export const STYLE_AJOUT =
  'rounded-sm bg-rouge-tint px-1 text-inherit data-[evidence=true]:outline-3 data-[evidence=true]:outline-bleu-nuit data-[evidence=true]:outline-offset-2';

interface MarqueAjoutProps {
  ajout: AjoutControle;
  children: ReactNode;
}

/**
 * An addition of the translation (#23, indicator 2.3 of the control), marked in the translated
 * text of "Avant / après" and explained by a bubble with what Tahiti says about it: its type
 * (the code with spaces), the word "ajout" (the status of the indicator is not said, #30) and its
 * `detail` as it is. When the
 * translation declares a change entirely inside it, the addition wins (its tint, its bubble) and
 * the bubble says the change in one line (#28).
 */
export function MarqueAjout({ ajout, children }: MarqueAjoutProps) {
  return (
    <MarqueBulle
      nom={`Ajout : ${ajout.type.libelle}`}
      className={STYLE_AJOUT}
      attribut="data-ajout"
      bulle={
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <PuceCode>{ajout.type.libelle}</PuceCode>
            <span className="font-semibold text-caption text-gray-700">ajout</span>
          </div>
          {ajout.detail === undefined ? null : <p className="mt-2 text-gray-700">{ajout.detail}</p>}
        </>
      }
    >
      {children}
    </MarqueBulle>
  );
}
